import OneSignal from 'react-onesignal';
import { soundManager } from './soundManager';
import { vibrationManager } from './vibrationManager';

const ONESIGNAL_APP_ID = import.meta.env.VITE_ONESIGNAL_APP_ID;

// Flag to prevent double initialization
let isInitialized = false;

export async function initializeOneSignal() {
  if (!ONESIGNAL_APP_ID) {
    console.warn('OneSignal App ID not configured');
    return;
  }
  
  // Prevent double initialization
  if (isInitialized) {
    console.log('OneSignal already initialized, skipping...');
    return;
  }

  try {
    await OneSignal.init({
      appId: ONESIGNAL_APP_ID,
      allowLocalhostAsSecureOrigin: true,
      serviceWorkerParam: {
        scope: '/',
      },
      serviceWorkerPath: '/OneSignalSDKWorker.js',
      notifyButton: {
        enable: false, // We'll use custom UI
      } as any,
    });
    
    // Mark as initialized
    isInitialized = true;

    // Add notification click handler
    OneSignal.Notifications.addEventListener('click', (event) => {
      console.log('Notification clicked:', event);
      
      // Get the URL from notification data
      const url = event.notification.launchURL || (event.notification as any)?.data?.url;
      
      if (url) {
        // Navigate to the URL
        window.location.href = url;
      }
    });

    // Add foreground notification handler to play custom sounds
    OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event) => {
      console.log('Foreground notification received:', event);
      
      // Get notification type from data
      const notificationData = event.notification.additionalData || {};
      const notificationType = (notificationData as any).type || 'general';
      
      // Map notification types to sound types
      let soundType: 'approved' | 'rejected' | 'assigned' | 'general' = 'general';
      
      if (notificationType.includes('approved') || notificationType.includes('accept')) {
        soundType = 'approved';
      } else if (notificationType.includes('rejected') || notificationType.includes('reject')) {
        soundType = 'rejected';
      } else if (notificationType.includes('assigned') || notificationType.includes('assign')) {
        soundType = 'assigned';
      }
      
      console.log(`Playing sound for notification type: ${soundType}`);
      
      // Play custom sound
      soundManager.play(soundType);
      
      // Trigger vibration
      vibrationManager.vibrate(soundType);
    });

    console.log('OneSignal initialized successfully');

    // Automatically request notification permission after initialization
    // Wait a bit for OneSignal to be fully ready
    setTimeout(async () => {
      try {
        const currentPermission = await OneSignal.Notifications.permission;
        
        // Only request if permission is still default (not asked yet)
        if (currentPermission === false) {
          console.log('Requesting notification permission...');
          const granted = await OneSignal.Notifications.requestPermission();
          console.log('Notification permission:', granted ? 'granted' : 'denied');
        } else {
          console.log('Notification permission already set:', currentPermission);
        }
      } catch (error) {
        console.error('Failed to request notification permission:', error);
      }
    }, 2000); // Wait 2 seconds for OneSignal to be fully ready
  } catch (error) {
    console.error('Failed to initialize OneSignal:', error);
    // Reset flag on error so it can be retried
    isInitialized = false;
  }
}

export async function requestNotificationPermission(): Promise<boolean> {
  try {
    const permission = await OneSignal.Notifications.requestPermission();
    return permission;
  } catch (error) {
    console.error('Failed to request notification permission:', error);
    return false as any;
  }
}

export async function getNotificationPermission(): Promise<NotificationPermission> {
  try {
    const permission = await OneSignal.Notifications.permission;
    return permission as unknown as NotificationPermission;
  } catch (error) {
    console.error('Failed to get notification permission:', error);
    return 'default';
  }
}

export async function getUserId(): Promise<string | null> {
  try {
    const userId = await OneSignal.User.PushSubscription.id;
    return userId;
  } catch (error) {
    console.error('Failed to get user ID:', error);
    return null;
  }
}

export async function getPlayerId(): Promise<string | null> {
  try {
    // Get OneSignal player ID (subscription ID)
    const playerId = await OneSignal.User.PushSubscription.id;
    return playerId;
  } catch (error) {
    console.error('Failed to get OneSignal player ID:', error);
    return null;
  }
}

export async function setExternalUserId(userId: string) {
  // [BUG-ONESIGNAL-1 FIX] OneSignal.login() crashes with TypeError reading 'Qe' (undefined)
  // when called before SDK internal state is ready (race condition on page load).
  // Root cause: AuthContext calls setExternalUserId immediately after user loads,
  // but OneSignal.init() is async and SDK internal objects may not be ready yet.
  // Fix: check window.OneSignal exists AND isInitialized before calling login();
  // if not ready, retry once after 3s delay. Wrap in try/catch to prevent crash propagation.
  const attemptLogin = async () => {
    try {
      // Guard: only call login if OneSignal SDK is fully initialized
      if (!isInitialized || !(window as any).OneSignal) {
        console.warn('[OneSignal] setExternalUserId called before SDK ready, skipping (will retry)');
        return false;
      }
      await OneSignal.login(userId);
      console.log('OneSignal external user ID set:', userId);
      return true;
    } catch (error) {
      // Swallow error — OneSignal crash must NEVER propagate to break page rendering
      console.warn('OneSignal setExternalUserId failed (non-fatal):', error);
      return false;
    }
  };

  // First attempt
  const ok = await attemptLogin();
  if (!ok) {
    // Retry after 3s — by then OneSignal.init() should be complete
    setTimeout(() => { attemptLogin(); }, 3000);
  }
}

export async function removeExternalUserId() {
  // [BUG-ONESIGNAL-1 FIX] Same guard as setExternalUserId — swallow errors to prevent crash propagation
  try {
    if (!isInitialized || !(window as any).OneSignal) {
      console.warn('[OneSignal] removeExternalUserId called before SDK ready, skipping');
      return;
    }
    await OneSignal.logout();
    console.log('OneSignal external user ID removed');
  } catch (error) {
    console.warn('OneSignal removeExternalUserId failed (non-fatal):', error);
  }
}

export async function sendTag(key: string, value: string) {
  try {
    await OneSignal.User.addTag(key, value);
    console.log(`OneSignal tag set: ${key}=${value}`);
  } catch (error) {
    console.error('Failed to send tag:', error);
  }
}

export async function deleteTag(key: string) {
  try {
    await OneSignal.User.removeTag(key);
    console.log(`OneSignal tag deleted: ${key}`);
  } catch (error) {
    console.error('Failed to delete tag:', error);
  }
}
